import { ChangeDetectorRef, Component } from '@angular/core';
import { FormGroup, Validators, FormBuilder, ValidationErrors, AbstractControl, AsyncValidatorFn } from '@angular/forms';
import { PACKAGE_SIZE, TRANSPORT_TYPE, ROUTE_TYPE, PROFILE_TYPE } from 'src/app/constants';
import { AlertService } from 'src/app/services/alert/alert.service';
import { Router } from '@angular/router';
import * as moment from 'moment';
import { setFormTouched } from 'src/app/utils/form';
import { Platform } from '@ionic/angular';
import { GoogleAnalyticsService } from 'src/app/services/google-analytics/google-analytics.service'
import { FirebaseService } from 'src/app/services/firebase-analytics/firebase.service';
import { Profile } from 'src/app/models/profile';
import { ProfileService } from 'src/app/services/profile/profile.service';
import { Observable } from 'rxjs';
import { map, first } from 'rxjs/operators';

const INPUT_FORMAT = 'DD/MM/YYYY';

@Component({
  selector: 'app-carry',
  templateUrl: './carry.page.html',
  styleUrls: ['./carry.page.scss'],
})
export class CarryPage {

  form: FormGroup;
  packageSizes = Object.keys(PACKAGE_SIZE);
  transportTypes = Object.keys(TRANSPORT_TYPE).filter(key => key !== TRANSPORT_TYPE.all);
  minDate = new Date();

  profile: Profile;
  
  constructor(
    private alert: AlertService,
    private fb: FormBuilder,
    private router: Router,
    private platform: Platform,
    private googleAnalyticsService: GoogleAnalyticsService,
    private firebaseService: FirebaseService,
    private profileService: ProfileService,
  ) {
    this.profileService.profile$.subscribe((profile) => { this.profile = profile });

    this.form = this.fb.group({
      type: [ROUTE_TYPE.send, Validators.required],
      from: ['', [], this.asyncMutexValidator()],
      to: ['', [], this.asyncMutexValidator()],
      date: ['', Validators.required],
      transportType: [],
      packageSize: [],
    });
    this.selectedDate(moment());

    this.observeFormValidation();
  }

  selectedFrom(place) {
    const { description = '' } = place || {};
    this.form.get('from').setValue(description);
  }

  selectedTo(place) {
    const { description = '' } = place || {};
    this.form.get('to').setValue(description);
  }

  hasSelectedFrom() {
    return !!this.form.get('from').value;
  }

  hasSelectedTo() {
    return !!this.form.get('to').value;
  }

  selectedDate(date: moment.Moment) {
    this.form.get(`date`).setValue(date.format(INPUT_FORMAT));
  }

  resetForm() {
    this.form.patchValue({
      from: '',
      to: '',
      date: '',
      transportType: null,
      packageSize: null,
    });
  }

  async onSubmit() {
    if (!this.form.valid) {
      setFormTouched(this.form);
      this.alert.toastWarning('WARNING_FORM_INVALID');
      return;
    }

    const params = this.form.value;
    params.date = moment(params.date, INPUT_FORMAT);

    const { transportType } = params;
    if (transportType === TRANSPORT_TYPE.all) {
      delete params.transportType;
    }

    this.router.navigate(['/find-send'], { queryParams: params });
  }

  offerCarryHitToGoogleAnalytics() {
    this.googleAnalyticsService.trackClickEvent('event', 'offer a carry from web', 'offer a carry', '', '', 0, false);
    this.firebaseService.firebaseLogEvent('click', { data: 'offer a carry from native' });
  }

  asyncMutexValidator(): AsyncValidatorFn  {
    return (formControl: AbstractControl): Observable<ValidationErrors[] | ValidationErrors | null> => {
      return this.profileService.profile$
        .pipe(
          map(profile => {
            if (!profile || (profile && (profile.type === PROFILE_TYPE.private || profile.type === PROFILE_TYPE.company))) {
              if (formControl.parent) {
                if (!formControl.value || formControl.value === '') {
                  return { required: true };
                } else {
                  return null;
                }
              }
            } else {
              if (formControl.parent) {
                const from = formControl.parent.get('from').value;
                const to = formControl.parent.get('to').value;
                if ((!from || from === '') && (!to || to === '')) {
                  return { fromToMutex: true };
                } else {
                  return null;
                }
              }
            }
          }
        ), first()
      )
    }
  }

  observeFormValidation() {
    const from = this.form.get('from');
    const to = this.form.get('to');
    from.statusChanges.subscribe(() => {
      to.updateValueAndValidity({ onlySelf: true, emitEvent: false });
      to.markAsTouched({ onlySelf: true });
    });
    to.statusChanges.subscribe(() => {
      from.updateValueAndValidity({ onlySelf: true, emitEvent: false });
      to.markAsTouched({ onlySelf: true });
    });
  }
  
  isNotProfessional() {
    return !this.profile || (this.profile && (this.profile.type === PROFILE_TYPE.private || this.profile.type === PROFILE_TYPE.company));
  }

}
